import React, { useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

// Reperes OMS (Child Growth Standards) - percentiles 3 / 50 / 97, par mois d'age.
// Sources : cdn.who.int (weight-for-age et length/height-for-age, garcons/filles, 0-5 ans).
const WFA_BOYS = { 0: [2.5, 3.3, 4.3], 1: [3.4, 4.5, 5.7], 2: [4.4, 5.6, 7.0], 3: [5.1, 6.4, 7.9], 6: [6.4, 7.9, 9.7], 9: [7.2, 8.9, 10.9], 12: [7.8, 9.6, 11.8], 18: [8.9, 10.9, 13.5], 24: [9.8, 12.2, 15.1], 36: [11.4, 14.3, 18.0], 48: [12.9, 16.3, 20.9], 60: [14.3, 18.3, 23.8] };
const WFA_GIRLS = { 0: [2.4, 3.2, 4.2], 1: [3.2, 4.2, 5.4], 2: [4.0, 5.1, 6.5], 3: [4.6, 5.8, 7.4], 6: [5.8, 7.3, 9.2], 9: [6.6, 8.2, 10.4], 12: [7.1, 8.9, 11.3], 18: [8.2, 10.2, 13.0], 24: [9.2, 11.5, 14.6], 36: [11.0, 13.9, 17.8], 48: [12.5, 16.1, 21.1], 60: [14.0, 18.2, 24.4] };
const LHFA_BOYS = { 0: [46.3, 49.9, 53.4], 1: [51.1, 54.7, 58.4], 2: [54.7, 58.4, 62.2], 3: [57.6, 61.4, 65.3], 6: [63.6, 67.6, 71.6], 9: [67.7, 72.0, 76.2], 12: [71.3, 75.7, 80.2], 18: [77.2, 82.3, 87.3], 24: [82.1, 87.8, 93.6], 36: [89.1, 96.1, 103.1], 48: [95.4, 103.3, 111.2], 60: [101.2, 110.0, 118.7] };
const LHFA_GIRLS = { 0: [45.6, 49.1, 52.7], 1: [50.0, 53.7, 57.4], 2: [53.2, 57.1, 60.9], 3: [55.8, 59.8, 63.8], 6: [61.5, 65.7, 70.0], 9: [65.6, 70.1, 74.7], 12: [69.2, 74.0, 78.9], 18: [75.2, 80.7, 86.2], 24: [80.3, 86.4, 92.5], 36: [87.9, 95.1, 102.2], 48: [94.6, 102.7, 110.8], 60: [100.5, 109.4, 118.4] };

const CHECKPOINTS = [0, 1, 2, 3, 6, 9, 12, 18, 24, 36, 48, 60];

// Interpolation lineaire entre les 2 points de reperes OMS les plus proches
const interpolate = (table, months) => {
  const m = Math.max(0, Math.min(60, months));
  let lo = CHECKPOINTS[0];
  let hi = CHECKPOINTS[CHECKPOINTS.length - 1];
  for (let i = 0; i < CHECKPOINTS.length - 1; i++) {
    if (m >= CHECKPOINTS[i] && m <= CHECKPOINTS[i + 1]) {
      lo = CHECKPOINTS[i];
      hi = CHECKPOINTS[i + 1];
      break;
    }
  }
  const [p3lo, p50lo, p97lo] = table[lo];
  const [p3hi, p50hi, p97hi] = table[hi];
  const ratio = hi === lo ? 0 : (m - lo) / (hi - lo);
  return [
    p3lo + (p3hi - p3lo) * ratio,
    p50lo + (p50hi - p50lo) * ratio,
    p97lo + (p97hi - p97lo) * ratio,
  ];
};

const evaluate = (value, [p3, p50, p97], t) => {
  if (value < p3) return { label: t('growth_below_who'), color: '#EF4444' };
  if (value > p97) return { label: t('growth_above_who'), color: '#F59E0B' };
  if (value < p50) return { label: t('growth_below_median'), color: '#10B981' };
  return { label: t('growth_above_median'), color: '#10B981' };
};

const GrowthTracker = () => {
  const { t } = useLanguage();
  const [sex, setSex] = useState('');
  const [ageMonths, setAgeMonths] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');

  const months = parseFloat(ageMonths);
  const w = parseFloat(weight);
  const h = parseFloat(height);
  const validAge = months >= 0 && months <= 60 && sex;

  const weightTable = sex === 'garcon' ? WFA_BOYS : WFA_GIRLS;
  const heightTable = sex === 'garcon' ? LHFA_BOYS : LHFA_GIRLS;

  const weightRefs = validAge ? interpolate(weightTable, months) : null;
  const heightRefs = validAge ? interpolate(heightTable, months) : null;

  const weightEval = validAge && w > 0 ? evaluate(w, weightRefs, t) : null;
  const heightEval = validAge && h > 0 ? evaluate(h, heightRefs, t) : null;

  return (
    <ToolPageLayout
      title={t('growth_title')}
      subtitle={t('growth_subtitle')}
      specialty="pediatre"
    >
      <div className="bg-[#E6F4FB] border border-[#0596DE]/20 rounded-2xl p-4 flex gap-3 mb-8">
        <TrendingUp className="w-5 h-5 text-[#0596DE] shrink-0 mt-0.5" />
        <p className="text-sm text-[#0F172A]">
          {t('growth_info')}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('growth_sex_label')}</Label>
          <select
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
          >
            <option value="">{t('growth_sex_placeholder')}</option>
            <option value="garcon">{t('growth_sex_boy')}</option>
            <option value="fille">{t('growth_sex_girl')}</option>
          </select>
        </div>
        <div>
          <Label className="mb-1 block">{t('growth_age_label')}</Label>
          <Input
            type="number"
            min="0"
            max="60"
            value={ageMonths}
            onChange={(e) => setAgeMonths(e.target.value)}
            placeholder={t('growth_age_placeholder')}
            className="rounded-xl"
          />
        </div>
        <div>
          <Label className="mb-1 block">{t('growth_weight_label')}</Label>
          <Input type="number" min="1" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('growth_height_label')}</Label>
          <Input type="number" min="30" step="0.1" value={height} onChange={(e) => setHeight(e.target.value)} className="rounded-xl" />
        </div>
      </div>

      {(weightEval || heightEval) && (
        <div className="grid sm:grid-cols-2 gap-4">
          {weightEval && (
            <div
              className="rounded-2xl p-5 border text-center"
              style={{ borderColor: `${weightEval.color}40`, backgroundColor: `${weightEval.color}0D` }}
            >
              <p className="text-sm text-[#64748B] mb-1">{t('growth_weight_result_label')} ({w} kg)</p>
              <p className="font-bold" style={{ color: weightEval.color }}>{weightEval.label}</p>
              <p className="text-xs text-[#94A3B8] mt-2">
                {t('growth_who_refs_at_age')} {weightRefs[0].toFixed(1)} – {weightRefs[2].toFixed(1)} kg ({t('growth_median_label')} {weightRefs[1].toFixed(1)})
              </p>
            </div>
          )}
          {heightEval && (
            <div
              className="rounded-2xl p-5 border text-center"
              style={{ borderColor: `${heightEval.color}40`, backgroundColor: `${heightEval.color}0D` }}
            >
              <p className="text-sm text-[#64748B] mb-1">{t('growth_height_result_label')} ({h} cm)</p>
              <p className="font-bold" style={{ color: heightEval.color }}>{heightEval.label}</p>
              <p className="text-xs text-[#94A3B8] mt-2">
                {t('growth_who_refs_at_age')} {heightRefs[0].toFixed(1)} – {heightRefs[2].toFixed(1)} cm ({t('growth_median_label')} {heightRefs[1].toFixed(1)})
              </p>
            </div>
          )}
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('growth_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default GrowthTracker;
